import { UserInfo } from '../hooks/utils/UseAuthService';
import IUserMenu from '../interfaces/IUserMenu';
import { IUserRole } from '../interfaces/IUserRole';

export const JWT_LOCAL_STORAGE_KEY = 'jwtToken';
export const ADMIN_JWT_LOCAL_STORAGE_KEY = 'adminJwtToken';
export const USERINFO_LOCAL_STORAGE_KEY = 'userinfo';
export const USER_ROLE_KEY = 'userRole';
export const PLATFORM_ID_KEY = 'platformId';
export const USER_MENUS_KEY = 'userMenus';
export const USER_PERMISSIONS_KEY = 'userPermissions';
export const USER_ROLES_KEY = 'userRoles';

type JwtTokenType = {
  aud: string;
  exp: number;
  iat: number;
  ste: number;
  sub: string;
};

export const tokenService = {
  getToken() {
    const token = localStorage.getItem(JWT_LOCAL_STORAGE_KEY) || undefined;
    if (!token) {
      return null;
    }
    return token;
  },

  disposeAllTokens() {
    tokenService.disposeToken();
    tokenService.disposeAdminToken();
  },

  disposeToken() {
    this.deleteUserInfo();
    return localStorage.removeItem(JWT_LOCAL_STORAGE_KEY);
  },

  setToken(token: string) {
    return localStorage.setItem(JWT_LOCAL_STORAGE_KEY, token);
  },

  getAdminToken() {
    return localStorage.getItem(ADMIN_JWT_LOCAL_STORAGE_KEY);
  },

  disposeAdminToken() {
    return localStorage.removeItem(ADMIN_JWT_LOCAL_STORAGE_KEY);
  },

  setAdminToken(token: string) {
    return localStorage.setItem(ADMIN_JWT_LOCAL_STORAGE_KEY, token);
  },

  setUserInfo(userInfo: UserInfo | undefined) {
    return localStorage.setItem(
      USERINFO_LOCAL_STORAGE_KEY,
      JSON.stringify(userInfo ?? {}),
    );
  },
  getUserInfo() {
    return JSON.parse(localStorage.getItem(USERINFO_LOCAL_STORAGE_KEY) ?? '{}');
  },
  deleteUserInfo() {
    return localStorage.removeItem(USERINFO_LOCAL_STORAGE_KEY);
  },

  setUserRole(role: IUserRole | null) {
    return localStorage.setItem(USER_ROLE_KEY, JSON.stringify(role));
  },

  getUserRole(): IUserRole | null {
    const stored = localStorage.getItem(USER_ROLE_KEY);
    return stored ? JSON.parse(stored) : null;
  },

  deleteUserRole() {
    return localStorage.removeItem(USER_ROLE_KEY);
  },

  setPlatformId(platformId: number) {
    return localStorage.setItem(PLATFORM_ID_KEY, JSON.stringify(platformId));
  },

  getPlatformId(): number | null {
    const stored = localStorage.getItem(PLATFORM_ID_KEY);
    return stored ? JSON.parse(stored) : null;
  },

  deletePlatformId() {
    return localStorage.removeItem(PLATFORM_ID_KEY);
  },

  setUserMenus(menus: IUserMenu[] | []) {
    return localStorage.setItem(USER_MENUS_KEY, JSON.stringify(menus));
  },

  getUserMenus(): IUserMenu[] {
    const stored = localStorage.getItem(USER_MENUS_KEY);
    return stored ? JSON.parse(stored) : [];
  },

  deleteUserMenus() {
    return localStorage.removeItem(USER_MENUS_KEY);
  },

  setUserPermissions(permissions: string[] | []) {
    return localStorage.setItem(
      USER_PERMISSIONS_KEY,
      JSON.stringify(permissions),
    );
  },

  getUserPermissions(): string[] {
    const stored = localStorage.getItem(USER_PERMISSIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  },

  deleteUserPermissions() {
    return localStorage.removeItem(USER_PERMISSIONS_KEY);
  },

  setUserRoles(roles: IUserRole[] | []) {
    return localStorage.setItem(USER_ROLES_KEY, JSON.stringify(roles));
  },

  getUserRoles(): IUserRole[] {
    const stored = localStorage.getItem(USER_ROLES_KEY);
    return stored ? JSON.parse(stored) : [];
  },

  deleteUserRoles() {
    return localStorage.removeItem(USER_ROLES_KEY);
  },

  parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    return JSON.parse(jsonPayload) as JwtTokenType;
  },

  getUserId(token: string | undefined) {
    if (!token) {
      return undefined;
    }
    try {
      const parsed = tokenService.parseJwt(token);
      const result = Number(parsed.sub);
      return Number.isNaN(result) ? undefined : result;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return undefined;
    }
  },
};
